import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useContext } from "react";
import specialOnboardingIcon from "../../../../assets/images/special-onboarding-icon.svg";
import VerificationIcon from "../../../../components/element/VerificationIcon";
import { useFormData } from "../../../../helpers/FormDataProvider";
import { StorageContext } from "../../../../services/StorageProvider";
import {
  AirportType,
  ClearanceStatus,
  DATE_AND_TIME_FORMAT,
  getStatusInfo,
} from "../../../../utils/constants";
import {
  getAirlineName,
  getAirportCode,
  getAirportData,
  getFlightNumber,
} from "../../../../utils/utils";
import "../ViewSpecialOnBoarding.css";

dayjs.extend(utc);

const SpecialOnBoardingCardComponent = ({
  item,
  onClickCard,
  escortArrivalStatus,
  escortDepartureStatus,
}) => {
  const { airportData, airlineData } = useContext(StorageContext);
  const { userData } = useFormData();

  // Extract flight plan data from the nested structure
  const flightPlan = item.cfp || item;

  const getAirportName = (airportId) => {
    const airport = getAirportData(airportData, airportId);
    return getAirportCode(airport);
  };

  const getAirportFullName = (airportId) => {
    const airport = getAirportData(airportData, airportId);
    return airport?.name;
  };

  // Helper function to get airport data by ID (using common utils)
  const getAirportById = (airportId) => getAirportData(airportData, airportId);

  // Helper function to check if airport is DOM type
  const isDOMAirport = (airportId) => {
    const airport = getAirportById(airportId);
    return airport?.airportType === AirportType.DOMESTIC;
  };

  // Helper function to get terminal name by ID
  const getTerminalName = (airportId, terminalId) => {
    const airport = getAirportById(airportId);
    if (!airport || !airport.terminals) return "";
    const terminal = airport.terminals.find((t) => t.id === terminalId);
    return terminal?.name || "";
  };

  // Helper function to get default terminal for DOM airports
  const getDefaultTerminal = () => {
    // When no terminal is selected, show "Main Terminal" as default
    return "Main Terminal";
  };

  // Function to get departure terminal
  const getDepartureTerminal = () => {
    if (!isDOMAirport(flightPlan.departureAirportId)) return null;

    const airport = getAirportById(flightPlan.departureAirportId);
    if (!airport) return null;

    // If there's a specific terminal ID, use it (check for undefined, null, and empty string)
    if (
      flightPlan.departureAirportTerminalId &&
      flightPlan.departureAirportTerminalId !== ""
    ) {
      const terminalName = getTerminalName(
        flightPlan.departureAirportId,
        flightPlan.departureAirportTerminalId
      );
      if (terminalName) return terminalName;
    }

    // Otherwise, show "Main Terminal" as default
    return getDefaultTerminal();
  };

  // Function to get arrival terminal
  const getArrivalTerminal = () => {
    if (!isDOMAirport(flightPlan.arrivalAirportId)) return null;

    const airport = getAirportById(flightPlan.arrivalAirportId);
    if (!airport) return null;

    // If there's a specific terminal ID, use it (check for undefined, null, and empty string)
    if (
      flightPlan.arrivalAirportTerminalId &&
      flightPlan.arrivalAirportTerminalId !== ""
    ) {
      const terminalName = getTerminalName(
        flightPlan.arrivalAirportId,
        flightPlan.arrivalAirportTerminalId
      );
      if (terminalName) return terminalName;
    }

    // Otherwise, show "Main Terminal" as default
    return getDefaultTerminal();
  };

  // Process escort status similar to EditFlightSpecialOnboarding
  const processedArrivalStatus =
    escortArrivalStatus === ClearanceStatus.NOT_REQUIRED
      ? null
      : escortArrivalStatus;
  const processedDepartureStatus =
    escortDepartureStatus === ClearanceStatus.NOT_REQUIRED
      ? null
      : escortDepartureStatus;

  const { text: arrivalStatusText } = getStatusInfo(processedArrivalStatus);
  const { text: departureStatusText } = getStatusInfo(processedDepartureStatus);

  return (
    <div className="view-special-onboarding-card" onClick={onClickCard}>
      <div className="view-special-onboarding-card-header">
        <span className="flight-code">{getFlightNumber(flightPlan)}</span>
        {flightPlan.requiresEscortService && (
          <img src={specialOnboardingIcon} alt="special-onboarding-icon" />
        )}
      </div>
      <div className="view-special-onboarding-card-airline-name">
        <span>{getAirlineName(airlineData, flightPlan?.airlineId)}</span>
        {(() => {
          const departureTerminal = getDepartureTerminal();
          const arrivalTerminal = getArrivalTerminal();

          // Show terminal for DOM airports
          if (
            isDOMAirport(flightPlan.departureAirportId) &&
            departureTerminal
          ) {
            return (
              <span className="departure-terminal">{departureTerminal}</span>
            );
          }

          if (isDOMAirport(flightPlan.arrivalAirportId) && arrivalTerminal) {
            return (
              <span className="departure-terminal">{arrivalTerminal}</span>
            );
          }

          return null;
        })()}
      </div>
      {/* Escort Status Section */}
      {(processedDepartureStatus != null || processedArrivalStatus != null) && (
        <div className="view-special-onboarding-card-status">
          <div className="status-row">
            {processedDepartureStatus != null && (
              <p className="status-p status-left">
                {getAirportFullName(flightPlan?.departureAirportId)} •{" "}
                <span
                  className={`status-with-icon-${
                    getStatusInfo(processedDepartureStatus).className
                  }`}
                >
                  {departureStatusText}
                </span>
              </p>
            )}
            {processedArrivalStatus != null && (
              <p className="status-p status-right">
                {getAirportFullName(flightPlan?.arrivalAirportId)} •{" "}
                <span
                  className={`status-with-icon-${
                    getStatusInfo(processedArrivalStatus).className
                  }`}
                >
                  {arrivalStatusText}
                </span>
              </p>
            )}
          </div>
        </div>
      )}
      <div className="view-special-onboarding-card-divider"></div>
      <div className="view-special-onboarding-card-details">
        <div className="view-special-onboarding-card-detail-item">
          <span className="view-special-onboarding-card-details-label">
            Departure
          </span>
          <span className="view-special-onboarding-card-details-value ">
            {`${getAirportName(flightPlan?.departureAirportId)} - ${dayjs(
              flightPlan.departureTime
            ).format(DATE_AND_TIME_FORMAT)}`}{" "}
          </span>
        </div>
        <div className="view-special-onboarding-card-detail-item">
          <span className="view-special-onboarding-card-details-label">
            Arrival
          </span>
          <span className="view-special-onboarding-card-details-value ">
            {`${getAirportName(flightPlan?.arrivalAirportId)} - ${dayjs(
              flightPlan.arrivalTime
            ).format(DATE_AND_TIME_FORMAT)}`}{" "}
          </span>
        </div>
        <div className="view-special-onboarding-card-detail-item">
          <span className="view-special-onboarding-card-details-label">
            Passenger Count
          </span>
          <span className="view-special-onboarding-card-details-value ">
            {flightPlan.passengerBoardingCount}
          </span>
        </div>
      </div>
      {userData?.isAAUser && (
        <VerificationIcon
          adsbVerified={item?.adsbVerified}
          bmsVerified={item?.bmsVerified}
          className="view-special-onboarding-card-verification-icon"
        />
      )}
    </div>
  );
};

export default SpecialOnBoardingCardComponent;
